// frontend/src/components/OctoAppTypeForm.js
import React, {useState} from 'react';
import {OverlayTrigger, Popover} from 'react-bootstrap';

const popoverOctoVersion = (
    <Popover id="popover-basic">
        <Popover.Body>
            Version of Octo+ to deploy. This field is mandatory <br/><br/>
            You can deploy a release version by entering two first digit of the version. For example "8.0" will deploy
            the last "8.0.X" <br/><br/>
            You can deploy a Change set version by entering the associated jira ticket number. For example ORET-8022
            will deploy the last version of the ticket.
        </Popover.Body>
    </Popover>
);

const popoverInfraVersion = (
    <Popover id="popover-basic">
        <Popover.Body>
            Version of Octo+ Infrastructure to deploy. This field is optional <br/><br/>
            If empty, the last version of Octo+ Infrastructure related to Octo+ Version will be deployed.<br/><br/>
            If not empty you have to enter the exact version of Octo+ Infrastructure to deploy.
        </Popover.Body>
    </Popover>
);

const OctoAppTypeForm = ({
    currentPath,
    versionFields,
    handleVersionChange,
    triggerUpdateAppPipeline,
    pipelineState,
    triggerOctoAppStartPipeline,
    triggerOctoAppStopPipeline,
    triggerOctoAppRestartPipeline,
    triggerArgoAppUpdatePipeline,
    muteAppAlerts,
    actionInProgress,
    goBack,
    otherEnvsVersion,
    envVersionsLoading,
    currentEnvInfos
}) => {
    const [error, setError] = useState("");

    const validateInputs = () => {
        const octoVersionPattern = /^\d+\.\d+\.\d+$/; // Matches "0.0.0" pattern
        if (octoVersionPattern.test(versionFields.octoVersion) && !versionFields.infraVersion) {
            setError("Infra Version cannot be empty when Octo Version matches the pattern X.X.X");
            return false;
        }
        setError("");
        return true;
    };

    const handleSubmit = () => {
        if (validateInputs()) {
            triggerUpdateAppPipeline();
        }
    };

    return (
        <div className="card shadow-sm border-0">
            <div className="p-3 border-bottom bg-white">
                <div className='d-flex flex-row align-items-center gap-3'>
                    {currentPath && (
                        <button className="btn btn-outline-secondary btn-sm d-flex align-items-center" type="button" onClick={goBack}>
                            <i className="bi bi-arrow-left me-2"></i>
                            Back
                        </button>
                    )}
                    <div>
                        <h4 className="h5 mb-0 text-dark">
                            <i className="bi bi-gear-fill me-2 text-secondary"></i>
                            Octo+ App: {currentPath.split('/').pop()}
                        </h4>
                        <small className="text-muted">{currentPath}</small>
                    </div>
                </div>
            </div>
            <div className="card-body p-4">
                <div className="row">
                    {/* Deploy Update Section */}
                    <div className="col-lg-6 mb-4">
                        <div className="card border-0 bg-light h-100">
                            <div className="card-body">
                                <h5 className="card-title">
                                    <i className="bi bi-upload me-2 text-primary"></i>
                                    Deploy Update
                                </h5>
                                <p className="text-muted small mb-4">Monitor the pipeline closely to ensure the update is operational.</p>
                                
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Octo Version:
                                        <OverlayTrigger trigger="hover" placement="right" overlay={popoverOctoVersion}>
                                            <i className="bi bi-question-circle ms-2 text-muted"></i>
                                        </OverlayTrigger>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={versionFields.octoVersion}
                                        onChange={(e) => handleVersionChange("octoVersion", e.target.value)}
                                        placeholder="8.0, ORET-8022 ..."
                                    />
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label fw-bold">
                                        Infra Version (optional):
                                        <OverlayTrigger trigger="hover" placement="right" overlay={popoverInfraVersion}>
                                            <i className="bi bi-question-circle ms-2 text-muted"></i>
                                        </OverlayTrigger>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={versionFields.infraVersion}
                                        onChange={(e) => handleVersionChange("infraVersion", e.target.value)}
                                        placeholder="Enter Infra Version"
                                    />
                                </div>
                                
                                {error && (
                                    <div className="alert alert-danger py-2 mb-3">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {error}
                                    </div>
                                )}
                                
                                <button
                                    className={`btn w-100 ${
                                        pipelineState === "idle"
                                            ? "btn-primary"
                                            : pipelineState === "loading"
                                                ? "btn-warning"
                                                : pipelineState === "success"
                                                    ? "btn-success"
                                                    : "btn-danger"
                                    } d-flex align-items-center justify-content-center`}
                                    onClick={handleSubmit}
                                    disabled={pipelineState === "loading"}
                                >
                                    {pipelineState === "loading" ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Triggering...
                                        </>
                                    ) : pipelineState === "success" ? (
                                        <>
                                            <i className="bi bi-check-circle-fill me-2"></i>
                                            Success
                                        </>
                                    ) : pipelineState === "failed" ? (
                                        <>
                                            <i className="bi bi-exclamation-circle-fill me-2"></i>
                                            Failed
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-play-circle me-2"></i>
                                            Trigger Pipeline
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div className="col-lg-6 mb-4">
                        <div className="card border-0 bg-light h-100">
                            <div className="card-body">
                                <h5 className="card-title">
                                    <i className="bi bi-tools me-2 text-primary"></i>
                                    Actions
                                </h5>
                                <p className="text-muted small mb-4">One rule: If you wonder what it's for, move along!</p>
                                
                                {!actionInProgress ? (
                                    <div className="d-grid gap-2">
                                        <button 
                                            className="btn btn-outline-dark d-flex align-items-center justify-content-center"
                                            type="button"
                                            onClick={muteAppAlerts}
                                        >
                                            <i className="bi bi-volume-mute-fill me-2"></i> 
                                            Mute Alerts
                                        </button>
                                        
                                        <button 
                                            className="btn btn-outline-info d-flex align-items-center justify-content-center"
                                            type="button"
                                            onClick={triggerArgoAppUpdatePipeline}
                                        >
                                            <i className="bi bi-arrow-clockwise me-2"></i> 
                                            Argo App Update
                                        </button>
                                        
                                        <button 
                                            className="btn btn-outline-success d-flex align-items-center justify-content-center"
                                            type="button"
                                            disabled={true}
                                            onClick={triggerOctoAppStartPipeline}
                                        >
                                            <i className="bi bi-play me-2"></i>
                                            Start (soon)
                                        </button>
                                        
                                        <button 
                                            className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                                            type="button"
                                            disabled={true}
                                            onClick={triggerOctoAppStopPipeline}
                                        >
                                            <i className="bi bi-stop me-2"></i>
                                            Stop (soon)
                                        </button>
                                        
                                        <button 
                                            className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                                            type="button"
                                            disabled={true}
                                            onClick={triggerOctoAppRestartPipeline}
                                        >
                                            <i className="bi bi-arrow-clockwise me-2"></i> 
                                            Restart (soon)
                                        </button>
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Processing...</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Versions Section */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card border-0 bg-light">
                            <div className="card-body">
                                <h5 className="card-title">
                                    <i className="bi bi-list-ul me-2 text-primary"></i>
                                    Environment Versions
                                </h5>
                                
                                {envVersionsLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Loading versions...</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead className="table-light">
                                                <tr>
                                                    <th scope="col">Environment</th>
                                                    <th scope="col">Octo Version</th>
                                                    <th scope="col">Infra Version</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentEnvInfos && (
                                                    <tr className="table-primary">
                                                        <th scope="row">
                                                            <i className="bi bi-star-fill me-2 text-warning"></i>
                                                            {currentEnvInfos.environment}
                                                        </th>
                                                        <td className="fw-bold">{currentEnvInfos.octoVersion}</td>
                                                        <td className="fw-bold">{currentEnvInfos.infraVersion}</td>
                                                    </tr>
                                                )}
                                                {otherEnvsVersion.map((item, index) => (
                                                    <tr key={index}>
                                                        <th scope="row">{item.environment}</th>
                                                        <td>{item.octoVersion}</td>
                                                        <td>{item.infraVersion}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OctoAppTypeForm;
